import useSwitch from "@mui/base/useSwitch";
import { useFirebaseRegistrationToken, useSubscriptionsList } from "@product/scmp-sdk";
import { useEffect, useState } from "react";
import type { FunctionComponent } from "react";
import { graphql, useFragment, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import { query } from "~/components/article/article-render/live-article";
import { Variant as LoadingVariant } from "~/components/common/loading/enums";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendOnePlusXEvent } from "~/components/tracking/one-plus-x";
import { getFirebaseApp } from "~/lib/firebase";
import { firebaseTopic, useNotificationPermission } from "~/lib/hooks";
import type { liveArticleQuery } from "~/queries/__generated__/liveArticleQuery.graphql";
import type { liveUpdateSwitchLiveArticleArticle$key } from "~/queries/__generated__/liveUpdateSwitchLiveArticleArticle.graphql";
import type { liveUpdateSwitchLiveArticleLiveArticle$key } from "~/queries/__generated__/liveUpdateSwitchLiveArticleLiveArticle.graphql";

import {
  Container,
  StyledLoading,
  SwitchInput,
  SwitchRoot,
  SwitchThumb,
  SwitchTrack,
  Text,
} from "./styles";

type Props = {
  className?: string;
  queryReference: PreloadedQuery<liveArticleQuery>;
  reference: liveUpdateSwitchLiveArticleArticle$key;
};

export const LiveArticleLiveUpdateSwitch: FunctionComponent<Props> = ({
  className,
  queryReference,
  reference,
}) => {
  const [fcmToken, setFcmToken] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { isGranted, requestPermission } = useNotificationPermission();

  const { registrationToken } = useFirebaseRegistrationToken(getFirebaseApp());

  const { setSubscriptionList, subscriptionList: subscriptionList_ } = useSubscriptionsList();

  const data = usePreloadedQuery<liveArticleQuery>(query, queryReference);
  const content = useFragment<liveUpdateSwitchLiveArticleLiveArticle$key>(
    graphql`
      fragment liveUpdateSwitchLiveArticleLiveArticle on LiveArticle {
        status
        pushNotifications
      }
    `,
    data.liveArticle,
  );

  const article = useFragment(
    graphql`
      fragment liveUpdateSwitchLiveArticleArticle on Article {
        entityId
      }
    `,
    reference,
  );

  const topic = `live-${article.entityId}`;

  const subscriptionList = subscriptionList_ ?? [];
  const isSubscribedToTopic = subscriptionList.includes(topic);

  const sendTrackings = (isChecked: boolean, topic: string, isFirstAllow = false) => {
    if (isFirstAllow) {
      sendGATracking({
        action: "Push Notification/Allow",
        category: "Push Notification PWA",
        label: topic,
      });
    }

    sendGATracking({
      action: `Live Blog Push PWA Notification/Switch ${isChecked ? "on" : "off"}`,
      category: "Live Blog Push PWA",
      label: window.location.href,
    });

    // Fire 1plusX event on switch on
    if (isChecked) {
      sendOnePlusXEvent({
        topic,
        type: "scmp-alerts",
      });
    }
  };

  useEffect(() => {
    // only execute when token is ready
    if (registrationToken.status !== "success" || !registrationToken.result) return;
    setFcmToken(registrationToken.result);
  }, [registrationToken]);

  const subscribeHandler = async (isChecked: boolean) => {
    const payload = {
      token: fcmToken,
      topic,
    };
    await (isChecked ? firebaseTopic.subscribe(payload) : firebaseTopic.unsubscribe(payload));

    setSubscriptionList(subscriptionList =>
      isChecked
        ? [...(subscriptionList ?? []), topic]
        : subscriptionList?.filter(value => value !== topic) ?? [],
    );
  };

  const onSwitchChangeHandler = async (isChecked: boolean) => {
    if (isGranted()) {
      sendTrackings(isChecked, topic);
      await subscribeHandler(isChecked);
    } else {
      const permissionResult = await requestPermission();
      if (permissionResult === "granted") {
        sendTrackings(isChecked, topic, true);
        await subscribeHandler(isChecked);
      }
    }
  };

  const { checked, getInputProps } = useSwitch({
    defaultChecked: isSubscribedToTopic,
    onChange: async event => {
      setIsLoading(true);
      await onSwitchChangeHandler(event.target.checked);
      setIsLoading(false);
    },
  });

  const renderComponent = () => {
    if (!content?.pushNotifications || !fcmToken || !article.entityId) return;
    return (
      <Container className={className}>
        <Text>Enable alerts for all live updates</Text>
        <SwitchRoot $isChecked={checked} $isHidden={isLoading}>
          <SwitchTrack>
            <SwitchThumb />
          </SwitchTrack>
          <SwitchInput {...getInputProps()} />
        </SwitchRoot>
        {isLoading && <StyledLoading variant={LoadingVariant.Spinner} />}
      </Container>
    );
  };

  return <>{renderComponent()}</>;
};

LiveArticleLiveUpdateSwitch.displayName = "LiveArticleLiveUpdateSwitch";
